import React, {useState, useMemo, useEffect, useCallback, useRef} from 'react'
import {useTable, useSortBy, usePagination, useRowSelect} from 'react-table'
import {PageWithText} from './../pagination'
import {FiChevronDown, FiChevronUp} from 'react-icons/fi'
import {OverlayLoader} from './../loader'
import {useHistory, useLocation, useRouteMatch} from "react-router-dom";
import qs from "qs";
import {useTranslation} from "react-i18next";
import styled from 'styled-components'
import {IconEdit, IconTeamInvitations, IconTeamMembers, IconTrash} from '../icons'


export const HeaderSmall = ({title, className = ''})=>{
	return (
		<small className={`font-bold ${className}`}>{title}</small>
	)
}

const MyCheckbox = styled.input`
    appearance: none;
    width: 16px;
    height: 16px;
    border: 1px solid var(--it-stroke-blue);
    background: var(--it-bg-cards);
    position: relative;
    transition: all 0.4s;
		
		display: flex;
		justify-content: center;
		align-items: center;

    &:hover {
        border: 1px solid var(--it-stroke-blue);
        background: var(--it-bg-blue);
    }

    &:checked {
        border: 1px solid var(--it-accent-blue);
        background: var(--it-accent-blue);
    }

    &:checked::before {
        content: "";
				width: 10px;
				height: 10px;
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'%3e%3cpath d='M3.906 7.66663L0.391602 4.15223L0.85552 3.68829L3.906 6.73835L9.14478 1.5L9.6087 1.96392L3.906 7.66663Z' fill='white'/%3e%3c/svg%3e");
        background-repeat: no-repeat;
    }
`

const IndeterminateCheckbox = React.forwardRef(
	({indeterminate, ...rest}, ref) => {
		const defaultRef = React.useRef()
		const resolvedRef = ref || defaultRef

		useEffect(() => {
			resolvedRef.current.indeterminate = indeterminate
			//console.log('IndeterminateCheckbox: ', {indeterminate, resolvedRef, rest});
		}, [resolvedRef, indeterminate])

		return (
			<MyCheckbox
				type="checkbox"
				ref={resolvedRef}
				{...rest}
			/>
		)
	}
)

const ActionLink = styled.a`
	color: var(--it-text-white);
	font-family: "Basis Grotesque Pro";
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%;
	letter-spacing: 0.42px;
	text-transform: uppercase;

	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: center;
	gap: 8px;

	border-radius: 0.5rem;
	padding: 0.25rem 0.5rem;

	background-color: transparent;
	cursor: pointer;
	text-decoration: inherit;
	outline: none;
	transition: all 0.4s;
		
	svg {
			path {
          transition: all 0.4s;
			}
	}

	&[data-action]:hover {
		color: var(--it-accent-blue);
			
		svg {
				path {
						fill: var(--it-accent-blue);
				}
		}
	}
	&[data-action-delete]:hover {
		color: var(--it-text-dark-red);
	}
`

const ActionLinkTitle = styled.span`
    &[data-change-order="true"] {
        order: -1;
    }

    &[data-lowercase="true"] {
        text-transform: lowercase;
    }
`

export const Action = ({
	id,
	recordData,
	title,
	Icon,
	changeTitleOrder = false,
  isTitleLowercase = false,
	color = 'blue',
	href = '',
	onHrefPrepare = (href, rowData) => href,
	onClick = (id, dataTable)=>{},
	canShow = (recordData, type)=>true,
	target,
	dataTable,
	className = '',
	style = {},
	type = Action.TYPE,
	...rest
})=> {
	let { url } = useRouteMatch();
	let history = useHistory();

	const _href = useMemo(()=>{
		if(!href) return false;

		return onHrefPrepare(href, recordData).replace(/:id/g,id).replace(/:url/g,url);
	}, [href, id, url, recordData]);

	const _onClick = useCallback(e => {
		if(_href && !_href.startsWith('http')){
			e.preventDefault();
			history.push(_href);
		}

		onClick && onClick(id, dataTable, recordData);
	}, [id, dataTable, _href, ])

	if(!canShow(recordData, type)) return null;

	let params = {
		target,
		onClick: _onClick,
		className,
		// className: `btn btn-sm btn-rounded btn-icon bg-transparent btn-start hover:bg-${color}-50 text-${color}-500 hover:text-${color}-600 ${className}`,
		...rest
	}
	if(_href) params.href = _href;

	return <ActionLink {...params} style={style}>
		{<Icon className={`stroke-current`} size={20} />}
		<ActionLinkTitle data-change-order={changeTitleOrder}
										 data-lowercase={isTitleLowercase}
		>
			{title}
		</ActionLinkTitle>
	</ActionLink>
}
Action.TYPE = 'general'

export const ActionEdit = ({title, href, ...rest})=> {
	const { t } = useTranslation()
  return <Action
		type={ActionEdit.TYPE}
		title={title === false ? '' : (title || t('datatable.action.edit'))}
		Icon={IconEdit}
		data-action
		{...rest}
		href={href || `:url/edit/:id`}
	/>
}
ActionEdit.TYPE = 'edit'

export const ActionDelete = ({title, ...rest})=> {
	const { t } = useTranslation()

	return <Action
		type={ActionDelete.TYPE}
		title={title || t('datatable.action.delete')}
		Icon={IconTrash}
		data-action-delete
		color="red"
		{...rest}
	/>
}
ActionDelete.TYPE = 'delete'

export const ActionAddInvitation = ({title, href, ...rest})=> {
	const { t } = useTranslation()
	return <Action
		type={ActionAddInvitation.TYPE}
		title={title || t('datatable.action.addInvitation')}
		Icon={IconTeamInvitations}
		data-action
		{...rest}
		href={href || `:url/../invitation/add/:id`}
	/>
}
ActionAddInvitation.TYPE = 'addInvitation'

export const ActionShowMembers = ({title, href, ...rest})=> {
	const { t } = useTranslation()
	return <Action
		type={ActionShowMembers.TYPE}
		title={title || t('datatable.action.showMembers')}
		Icon={IconTeamMembers}
		data-action
		{...rest}
		href={href || `:url/../member/:id`}
	/>
}
ActionShowMembers.TYPE = 'showMembers'

function getAccessors(columns, accessors = []){
	columns.forEach((column, idx)=>{
		if(column.accessor) accessors.push(column.accessor);
		if(column.columns) accessors = getAccessors(column.columns, accessors)
	})

	return accessors;
}

const Datatable = React.forwardRef((
	{
		columns,
		fetchData,
		pageIndex: initPageIndex = 0,
		pageSize: initPageSize = 25,
		sortBy: initSortBy = {id: 'desc'},
		canSelectRow = false,
		onRowSelect = (selectedRowsIds) => {},
		selectedRowIds = [],
		actions = [],
		filter = false,
		pageSizes = [1, 10, 25, 50, 100, 500],
		isTableBorders = false,
	},
	ref
) => {
	const location = useLocation()
	const history = useHistory();
	const mountedRef = useRef(true)

	let allAccessors = useMemo(()=>{
		return getAccessors(columns);
	}, [columns]);
	let initSort = useMemo(
		() => {
			let result = [];
			let querySort = qs.parse(location.search.split('?')[1])

			initPageIndex = querySort.page ? parseInt(querySort.page) : initPageIndex;
			initPageSize = querySort.limit ? parseInt(querySort.limit) : initPageSize;

			if (querySort.sort) {
				initSortBy = querySort.sort;
			}
			for (let k in initSortBy) {
				if (!initSortBy.hasOwnProperty(k)) continue;
				if (!allAccessors.includes(k)) continue;
				result.push({id: k, desc: initSortBy[k] === 'desc'})
			}

			return result;
		},
		[location.search, allAccessors]
	);

	const [isLoading, setLoading] = useState(false)
	const [pageInfo, setPageInfo] = useState({
		data: [],
		pageCount: 0,
		pageIndex: initPageIndex,
		pageSize: initPageSize,
	})
	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		page,
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		nextPage,
		previousPage,
		setPageSize,
		state: {pageIndex, pageSize, sortBy, selectedRowIds: _selectedRowIds}
	} = useTable(
		{
			columns,
			data: pageInfo.data,
			initialState: {
				pageIndex: pageInfo.pageIndex,
				pageSize: pageInfo.pageSize,
				sortBy: initSort,
				selectedRowIds: selectedRowIds,
			},
			pageCount: pageInfo.pageCount,
			manualPagination: true,
			manualSortBy: true,
			defaultCanSort: false,
			disableMultiSort: false,
			maxMultiSortColCount: 3,
			dataTable: ref?.current,
		},
		useSortBy,
		usePagination,
		useRowSelect,
		hooks => {
			if (canSelectRow) {
				hooks.visibleColumns.push(columns => [
					{
						id: 'selection',
						// The header can use the table's getToggleAllRowsSelectedProps method
						// to render a checkbox
						Header: ({getToggleAllRowsSelectedProps}) => (
							<>
								<IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
							</>
						),
						// The cell can use the individual row's getToggleRowSelectedProps method
						// to the render a checkbox
						Cell: ({row}) => (
							<>
								<IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
							</>
						)
					},
					...columns
				])
			}
			if(actions && actions.length > 0){
				hooks.visibleColumns.push(columns => [
					...columns,
					{
						id: 'actions',
						// The header can use the table's getToggleAllRowsSelectedProps method
						// to render a checkbox
						Header: 'Actions',
						// The cell can use the individual row's getToggleRowSelectedProps method
						// to the render a checkbox
						Cell: ({row}) => (
							<>
								{actions.map((ActionComp, k) => <ActionComp.type {...ActionComp.props} key={k} id={row.values.id} recordData={row.original} dataTable={ref.current} />)}
							</>
						)
					},
				])
			}
		}
	)

	let sortByMemo = useMemo(
		() => sortBy.reduce((result, item) => {
			result[item.id] = item.desc ? 'desc' : 'asc';
			return result;
		}, {}),
		[sortBy]
	);

	let loadPageInProgress = false;
	const loadPage = useCallback(() => {
		if(loadPageInProgress) return;
		loadPageInProgress = true;
		setLoading(true);
		fetchData({pageIndex, pageSize, sortBy: sortByMemo, filter})
			.then((data) => {
				loadPageInProgress = false;
				if (!mountedRef.current) return null
				setPageInfo({
					...pageInfo,
					data: data.data,
					pageCount: data?.meta?.last_page ?? 1,
					pageIndex: pageIndex,
					pageSize: pageSize,
				})
				setLoading(false);

				history.replace(`?${qs.stringify({
					page: pageIndex,
					limit: pageSize,
					sort: sortByMemo
				})}`, history.location.state)
			})
			.catch((e) => {
				loadPageInProgress = false;
				if (!mountedRef.current) return null
				console.log('DataContent.fetchData.error', e);
				setPageInfo({
					...pageInfo,
					pageIndex: pageIndex - 1,
				})
				setLoading(false);
			})
	}, [pageIndex, pageSize, sortByMemo, filter]);
	useEffect(()=>{
		if(!pageInfo?.data || !_selectedRowIds) return;
		let ids = Object.keys(_selectedRowIds || {}).filter((key)=>_selectedRowIds[key] && pageInfo?.data[key]).map((key, idx)=>{
			return pageInfo?.data[key].id;
		})
		onRowSelect(ids);
	}, [Object.keys(_selectedRowIds || {}).join(','), pageInfo?.data])
	useEffect(()=>{
		if(pageIndex === 0) return loadPage();
		gotoPage(0);
	}, [filter])
	useEffect(()=>{
		mountedRef.current = true;
		loadPage();
		return () => {
			mountedRef.current = false;
		}
	}, [pageIndex, pageSize, sortByMemo])

	React.useImperativeHandle(
		ref,
		() => ({
			refreshPage: loadPage
		}),
	)

	// Render the UI for your table
	return (
		<OverlayLoader show={isLoading}>
			<TableBox className={'scroll'}>
				<Table
					{...getTableProps()}
					// className="table"
					data-table-borders={!!isTableBorders}
				>
					<Thead>
						{
							headerGroups.map((headerGroup, i) => (
								<Tr
									key={`headerGroup-${i}`}
									data-header
									{...headerGroup.getHeaderGroupProps()}
								>
									{
										headerGroup.headers.map((column, j) => (
											<Th
												key={`column-${i}-${j}`}
												{...column.getHeaderProps([column.getSortByToggleProps(), column.headerProps || {},])}
												data-table-borders={!!isTableBorders}
											>
												{/* <div className="flex flex-row items-center justify-start">
													<span>{column.render('Header')}</span>
													<span className="ml-auto">
														{
															!column.isSorted
																? ''
																: column.isSortedDesc
																	? <FiChevronDown className="stroke-current text-2xs"/>
																	: <FiChevronUp className="stroke-current text-2xs"/>
														}
													</span>
												</div> */}
												<CellHead>
													<span data-title>{column.render('Header')}</span>
													<span data-sort>
														{
															!column.isSorted
																? ''
																: column.isSortedDesc
																	? <FiChevronDown className="stroke-current text-2xs"/>
																	: <FiChevronUp className="stroke-current text-2xs"/>
														}
													</span>
												</CellHead>
											</Th>
										))
									}
								</Tr>
							))
						}
					</Thead>
					<TBody 
						{...getTableBodyProps()}
					>
						{
							page.map((row, i) => {
								prepareRow(row)
								//console.log('Tbody, Tr: ', {row, getRowProps: row.getRowProps()});
								return (
									<Tr
										key={`page-row-${i}`}
										data-body
										className=""
										data-selected={row.isSelected ?? false}
										{...row.getRowProps()}
									>
										{
											row.cells.map((cell, idx) => {
												return (
													<Td key={`cell-${i}-${idx}`}
															{...cell.getCellProps()}
															data-table-borders={!!isTableBorders}
													>
														{cell.render('Cell')}
													</Td>
												)
											})
										}
									</Tr>
								)
							})
						}
					</TBody>
				</Table>
			</TableBox>

			{/* <div className="flex flex-row items-center justify-between my-4">
				<div className="flex flex-wrap items-center justify-start space-x-2 pagination">
					{pageIndex !== 0 && (
						<PageWithText onClick={() => gotoPage(0)}>First</PageWithText>
					)}
					{canPreviousPage && (
						<PageWithText onClick={() => previousPage()}>Previous</PageWithText>
					)}
					{canNextPage && (
						<PageWithText onClick={() => nextPage()} disabled={!canNextPage}>
							Next
						</PageWithText>
					)}
					{pageIndex !== pageCount - 1 && (
						<PageWithText
							onClick={() => gotoPage(pageCount - 1)}
							disabled={!canNextPage}>
							Last
						</PageWithText>
					)}
				</div>

				<span>
				Page{' '}
					<b>{pageIndex + 1} of {pageOptions.length}</b>{' '}
			</span>

				<select
					className="form-select text-sm bg-white dark:bg-grey-800 dark:border-grey-800 outline-none shadow-none focus:shadow-none"
					value={pageSize}
					onChange={e => {
						setPageSize(Number(e.target.value))
					}}>
					{pageSizes.map(pageSize => (
						<option key={pageSize} value={pageSize}>
							Show {pageSize}
						</option>
					))}
				</select>
			</div> */}
			<PageLine>
				<PageItems>
					{ pageIndex !== 0 && (<PageItem onClick={() => gotoPage(0)}>First</PageItem>) }
					{ canPreviousPage && (<PageItem onClick={() => previousPage()}>Previous</PageItem>) }
					{ canNextPage && (<PageItem onClick={() => nextPage()} disabled={!canNextPage}>Next</PageItem>) }
					{ pageIndex !== pageCount - 1 && (<PageItem onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>Last</PageItem>) }
				</PageItems>

				<span>Page&nbsp;<b>{pageIndex + 1} of {pageOptions.length}</b>&nbsp;</span>

				<PageRange>
					<select
						className="form-select text-sm bg-white dark:bg-grey-800 dark:border-grey-800 outline-none shadow-none focus:shadow-none"
						value={pageSize}
						onChange={e => setPageSize(Number(e.target.value))}>
						{pageSizes.map(pageSize => <option key={pageSize} value={pageSize}>Show {pageSize}</option>)}
					</select>
				</PageRange>
			</PageLine>
		</OverlayLoader>
	)
})

export default Datatable

const TableBox = styled.div`
	color: rgba(255, 255, 255, 0.60);
	font-family: 'Basis Grotesque Pro';
	font-size: 14px; // TODO check the general table font-size !
	font-style: normal;
	font-weight: 400;
	line-height: 100%; /* 12px */
	background: #061823; /** TODO: !!! move upper in DOM */

	overflow-x: auto;
	overflow-y: hidden; //TODO: check unless in unnecessary [for case below - offset for extra space between table header and body]
`
const Table = styled.table`
	box-sizing: border-box;
	border-collapse: separate;
	border-spacing: 0 6px;
	border-collapse: collapse;
	width: 100%;
`
const Thead = styled.thead`
`
const TBody = styled.tbody`
		// offset for extra space between table header and body	
    position: relative;
    top: 12px;
`
const Tr = styled.tr`
	&[data-header] {
      background: var(--it-bg-cards);
	}
	/*&[data-body]:nth-child(2n) {
		background: var(--it-bg-cards);
		border-bottom: 1px solid var(--it-stroke-grey);
	}*/

	&[data-body] {
			border-bottom: 1px solid var(--it-stroke-grey);
	}
		
	&[data-body]:hover, &[data-body][data-selected="true"] {
		background: var(--it-bg-cards);
		/* background: #061823 !important; // !!! color */
			
		.app-img-preview {
        &[data-grey-filter="true"] {
            filter: unset;
        }
		}
	}
`
const Th = styled.th`
	padding: 12px 10px;
	line-height: 100%; /* 12px */
	letter-spacing: 0.36px;
	text-transform: uppercase;
	font-size: 12px; // TODO check the general table header font-size !
		
	&[data-table-borders="true"] {
		border: 1px solid var(--it-stroke-grey);
	}
`
const Td = styled.td`
	padding: 12px 10px;

	&[data-table-borders="true"] {
			border: 1px solid var(--it-stroke-grey);
	}
		
	&:first-child {
		padding-left: 8px;
	}
	&:last-child {
		padding-right: 8px;
	}
	line-height: 130%; /* 15.6px */
	letter-spacing: 0.24px;
	& [data-strong] {
		color: #FFF;
		font-size: 14px;
		line-height: 120%; /* 16.8px */
		letter-spacing: 0.42px;
	}
	& [data-action], & [data-action-delete] {
		color: #FFF;
		font-size: 14px;
		line-height: 100%; /* 14px */
		letter-spacing: 0.42px;
		text-transform: uppercase;
	}
	& [data-action-delete] {
		font-weight: 700;
		color: var(--it-text-red);
	}
`
const CellHead = styled.div`
	display: flex;
	flex-flow: row nowrap;
	justify-content: flex-start;
	align-items: center;
	gap: 6px;
	& [data-title] {}
	& [data-sort] {}
`
const PageLine = styled.div`
	// compensation of 'offset for extra space between table header and body'	
	position: relative;
	top: 12px;	
		
	color: #FFF;
	background: #061823; /** TODO: !!! move upper in DOM */
	display: flex;
	flex-flow: row wrap;
	justify-content: space-between;
	align-items: center;
	margin: 8px 0;
`
const PageItems = styled.div`
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: center;
	gap: 12px;
	/* pagination */
`
const PageItem = styled.button`
	display: flex;
	height: 40px;
	padding: 0px 16px;
	justify-content: center;
	align-items: center;
	gap: 12px;
	flex: 1 0 0;

	border-radius: 1px;
	background: rgba(238, 238, 238, 0.05);

	color: #FFF;
	font-family: 'Basis Grotesque Pro';
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 100%; /* 16px */
	letter-spacing: 0.48px;

	/* btn
	btn-default
	bg-transparent 
	hover:bg-grey-200
	text-grey-900
	dark:text-white
.btn {
    font-weight: 700;
    text-transform: uppercase;
}
.text-grey-900 {
    --text-opacity: 1;
    color: #212121;
    color: rgba(33, 33, 33, var(--text-opacity));
}
.bg-transparent {
    background-color: transparent;
}
a, input[type="submit"], button {
    outline: 0;
    cursor: pointer;
}
*, input, select, textarea, option, button {
    outline: none !important;
}
	*/
`
const PageRange = styled.div`
	display: inline-flex;
	background: rgba(238, 238, 238, 0.05);
	& select {
		border: none;
		background: transparent;
		height: 40px;
		padding: 0px 16px;
		justify-content: space-between;
		align-items: center;
		flex: 1 0 0;
	}
	& option {
		border: none;
		background: transparent;
	}
`
